import {RawLocation} from 'vue-router';
import {SearchEntity} from '@/classes/dto/finder/request/Search.request.dto';
import {SearchHit} from '@/classes/dto/finder/response/Search.response.dto';

/** Areas of the palette: finder areas plus trainings (searched in the frontend via the event-manager) */
export type PaletteEntity = SearchEntity | 'trainings';

/** A hit of the palette; trainings are mapped to the finder hit shape */
export type PaletteHit = Omit<SearchHit, 'entity'> & {
    entity: PaletteEntity;
    /** Page opened instead of the profile (e.g. the acquisition list item of a recent visit) */
    openTo?: string;
    /** Label of the open button for `openTo` */
    openLabel?: string;
};

export type EntityConfig = {
    key: PaletteEntity;
    /** Plural label of the filter pill and group */
    label: string;
    /** Singular label shown in the quick view */
    singular: string;
    icon: string;
    /** Prefix shortcut (parsed by the finder; "sc" is handled by the palette) */
    prefix: string;
};

export const SEARCH_ENTITIES: EntityConfig[] = [
    {key: 'students', label: 'Schüler:innen', singular: 'Schüler:in', icon: 'mdi-school-outline', prefix: 's'},
    {key: 'parents', label: 'Eltern', singular: 'Elternteil', icon: 'mdi-account-child-outline', prefix: 'e'},
    {key: 'employees', label: 'Mitarbeitende', singular: 'Mitarbeiter:in', icon: 'mdi-account-tie-outline', prefix: 'm'},
    {key: 'teachers', label: 'Lehrkräfte', singular: 'Lehrkraft', icon: 'mdi-human-male-board', prefix: 'l'},
    {key: 'applicants', label: 'Bewerber:innen', singular: 'Bewerber:in', icon: 'mdi-account-search-outline', prefix: 'b'},
    {key: 'institutions', label: 'Standorte', singular: 'Standort', icon: 'mdi-domain', prefix: 'st'},
    {key: 'courses', label: 'Kurse', singular: 'Kurs', icon: 'mdi-table-account', prefix: 'k'},
    {key: 'trainings', label: 'Schulungen', singular: 'Schulung', icon: 'mdi-cast-education', prefix: 'sc'},
    {key: 'products', label: 'Produkte', singular: 'Produkt', icon: 'mdi-package-variant-closed', prefix: 'p'},
];

export const ENTITY_BY_KEY = SEARCH_ENTITIES.reduce((acc, el) => {
    acc[el.key] = el;
    return acc;
}, {} as Record<PaletteEntity, EntityConfig>);

/** Route of the profile / detail view of a hit; null if there is no view to open */
export function routeOfEntity(entity: PaletteEntity, id: string): RawLocation | null {
    switch (entity) {
        case 'students':
            return `/students/${id}`;
        case 'parents':
            return `/parents/${id}`;
        case 'employees':
            return `/employees/${id}`;
        case 'teachers':
            return `/teachers/${id}`;
        case 'applicants':
            return `/recruiter/applicants/${id}`;
        case 'institutions':
            return `/institutions/${id}`;
        case 'courses':
            return `/courses/${id}`;
        case 'trainings':
            return `/trainings/${id}`;
        case 'products':
            // Catalogue products have no detail view; the product list filtered to this product opens instead
            return {path: '/catalogue/products', query: {'q': '1', 'filter._id': JSON.stringify([id])}};
    }
    return null;
}

/**
 * Splits a search term into its prefix shortcut (parsed by the finder) and the rest ("st: Bruchsal" → st, Bruchsal)
 */
export function splitPrefix(term: string): { prefix: string | null; rest: string } {
    const match = /^\s*([a-z]{1,2}):\s*(.*)$/i.exec(term);
    if (match && SEARCH_ENTITIES.some((el) => el.prefix === match[1].toLowerCase())) {
        return {prefix: match[1].toLowerCase(), rest: match[2]};
    }
    return {prefix: null, rest: term};
}

export type TextSegment = { text: string; mark: boolean };

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Splits a text into marked and unmarked parts for every word of the term (rendered without v-html) */
export function highlightSegments(text: string | undefined | null, term: string): TextSegment[] {
    const value = text || '';
    const words = splitPrefix(term).rest.trim().split(/\s+/).filter((el) => el.length > 0).map(escapeRegExp);
    if (!value || !words.length) {
        return [{text: value, mark: false}];
    }
    const regExp = new RegExp(`(${words.join('|')})`, 'ig');
    return value.split(regExp).filter((el) => el.length > 0).map((part) => ({
        text: part,
        mark: words.some((word) => new RegExp(`^${word}$`, 'i').test(part)),
    }));
}

export function initialsOf(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return '?';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return `${first}${last}`.toUpperCase();
}

const NAME_FIELDS = ['name', 'title', 'firstname', 'lastname', 'fullname', 'displayname'];

const MATCH_LABELS: Array<{ test: RegExp; label: string }> = [
    {test: /^parents?\..*(phone|mobile)/i, label: 'Telefonnummer der Eltern'},
    {test: /^parents?\..*mail/i, label: 'E-Mail der Eltern'},
    {test: /^parents?\./i, label: 'Name der Eltern'},
    {test: /^(children|students?)\./i, label: 'Kind'},
    {test: /teacher/i, label: 'Lehrkraft'},
    {test: /institution/i, label: 'Standort'},
    {test: /customid|accountingref/i, label: 'Nummer'},
    {test: /phone|mobile|tel/i, label: 'Telefonnummer'},
    {test: /mail/i, label: 'E-Mail'},
    {test: /postal|zip|plz/i, label: 'PLZ'},
    {test: /city|town|ort/i, label: 'Ort'},
    {test: /street/i, label: 'Straße'},
    {test: /subject/i, label: 'Fach'},
    {test: /iban/i, label: 'IBAN'},
    {test: /responsible/i, label: 'Ansprechpartner:in'},
];

/** "gefunden über …" text from the matched fields, empty if the hit was found by its name */
export function matchedOnText(hit: Pick<SearchHit, 'matchedOn'>): string {
    const fields = (hit.matchedOn || []).filter((field) => !NAME_FIELDS.includes(field.split('.').pop()?.toLowerCase() || '') || field.includes('.'));
    if (!fields.length) {
        return '';
    }
    const labels: string[] = [];
    for (const field of fields) {
        const label = MATCH_LABELS.find((el) => el.test.test(field))?.label || field;
        if (!labels.includes(label)) {
            labels.push(label);
        }
    }
    return labels.join(', ');
}

/** Tone class (modern.css) of the status tag of a hit */
export function statusTone(hit: { entity: PaletteEntity | string; isActive?: boolean }): string {
    if (hit.isActive === false) {
        return 'tone-bad';
    }
    return hit.entity === 'applicants' ? 'tone-info' : 'tone-good';
}

export function statusText(hit: { isActive?: boolean; status?: string }): string {
    if (hit.status) {
        return hit.status;
    }
    if (hit.isActive === false) {
        return 'Inaktiv';
    }
    return '';
}
